/* eslint i18n-text/no-en: off */
import type AutocompleteElement from '@github/auto-complete-element'
import type DetailsDialogElement from '@github/details-dialog-element'
import {on} from 'delegated-events'
import {isEnterprise} from '@github-ui/runtime-environment'

function businessTwoFactorInput(): HTMLInputElement {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  return document.querySelector<HTMLInputElement>('.js-business-enable-two-factor')!
}

function twoFactorSecureMethodsPresent(): boolean {
  return document.querySelector<HTMLInputElement>('.js-business-two-factor-secure-methods-currently-enabled') != null
}

function enablingTwoFactor(): boolean {
  const twoFactorCurrentlyEnabled =
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    document.querySelector<HTMLInputElement>('.js-business-two-factor-currently-enabled')!.value === '0'
  return businessTwoFactorInput().checked && twoFactorCurrentlyEnabled
}

function twoFactorRequirementSettingChanged(): boolean {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const enabled = document.querySelector<HTMLInputElement>('.js-business-two-factor-currently-enabled')!.value === '1'
  const enabling = businessTwoFactorInput().checked
  return enabled !== enabling
}

function twoFactorRequirementSecureMethodSettingChanged(): boolean {
  const current = document.querySelector<HTMLInputElement>('.js-business-two-factor-secure-methods-currently-enabled')
  const checkbox = document.querySelector<HTMLInputElement>('.js-business-enable-two-factor-secure-methods')
  if (current == null || checkbox == null) {
    return false
  }

  const enabled = current.value === '1'
  const enabling = checkbox.checked
  return enabled !== enabling
}

// disable the 2FA requirement checkbox if the user has enabled the secure methods only checkbox
// don't use the .disabled property as that prevents the value from being submitted with the form
function setTwoFactorRequirementCheckboxState(requirementCheckbox: HTMLInputElement, isDisabled: boolean): void {
  if (isDisabled) {
    requirementCheckbox.readOnly = true
    requirementCheckbox.style.pointerEvents = 'none'
    requirementCheckbox.style.opacity = '0.5'
  } else {
    requirementCheckbox.readOnly = false
    requirementCheckbox.style.pointerEvents = 'auto'
    requirementCheckbox.style.opacity = '1'
  }
}

// update the 'submit' button state based on whether either of the two-factorsettings have changed
function updateTwoFactorRequirementSubmitButtonState(hasChanges: boolean) {
  // there are two possible submit buttons that get rendered
  // the submitFormButton is an invalid button that gets rendered when a user has clicked multiple times on the web page
  const submitButton = document.querySelector<HTMLButtonElement>('.js-business-two-factor-submit-button')

  if (submitButton == null) {
    return
  }
  if (hasChanges) {
    submitButton.style.pointerEvents = 'auto'
    submitButton.style.opacity = '1'
  } else {
    submitButton.style.pointerEvents = 'none'
    submitButton.style.opacity = '0.5'
  }
  submitButton.setAttribute('aria-disabled', (!hasChanges).toString())
}

function hideAllDialogContent() {
  for (const span of document.querySelectorAll<HTMLElement>('.js-business-two-factor-confirmation-dialog-body span')) {
    span.hidden = true
  }
}

function setDialogState(
  dialogTitle: HTMLElement,
  dialogButton: HTMLElement,
  titleText: string,
  elementId: string,
  isDanger: boolean,
) {
  dialogTitle.textContent = titleText
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  document.querySelector<HTMLElement>(elementId)!.hidden = false
  dialogButton.classList.remove(isDanger ? 'Button--primary' : 'Button--danger')
  dialogButton.classList.add(isDanger ? 'Button--danger' : 'Button--primary')
}

function updateTwoFactorConfirmationDialogForEnterprise() {
  const requirementChanged = twoFactorRequirementSettingChanged()
  if (!requirementChanged) {
    return
  }

  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const dialogTitle = document.querySelector<HTMLElement>('.js-business-two-factor-confirmation-dialog .Box-title')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const dialogButton = document.querySelector<HTMLElement>('.js-business-two-factor-confirmation-dialog-button')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const businessName = document.querySelector<HTMLInputElement>('#two-factor-business-name')!.value
  const requirementEnabled =
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    document.querySelector<HTMLInputElement>('.js-business-two-factor-currently-enabled')!.value === '1'

  const disablingRequirement = requirementEnabled && requirementChanged
  const enablingRequirement = !requirementEnabled && requirementChanged

  hideAllDialogContent()

  if (disablingRequirement) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to remove the two-factor authentication requirement for all organizations in ${businessName}?`,
      '#tfrc-disabling-requirement',
      true,
    )
  } else if (enablingRequirement) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to require two-factor authentication for all organizations in ${businessName}?`,
      '#tfrc-enabling-requirement-without-secure-methods',
      false,
    )
  }
}

function updateTwoFactorConfirmationDialogForDotcom() {
  if (!twoFactorSecureMethodsPresent()) {
    return
  }

  const requirementChanged = twoFactorRequirementSettingChanged()
  const secureMethodsChanged = twoFactorRequirementSecureMethodSettingChanged()
  if (!requirementChanged && !secureMethodsChanged) {
    return
  }

  const requirementEnabled =
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    document.querySelector<HTMLInputElement>('.js-business-two-factor-currently-enabled')!.value === '1'
  const secureMethodsEnabled =
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    document.querySelector<HTMLInputElement>('.js-business-two-factor-secure-methods-currently-enabled')!.value === '1'

  const disablingRequirement = requirementEnabled && requirementChanged
  const enablingRequirement = !requirementEnabled && requirementChanged
  const disablingSecureMethods = secureMethodsEnabled && secureMethodsChanged
  const enablingSecureMethods = !secureMethodsEnabled && secureMethodsChanged

  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const dialogTitle = document.querySelector<HTMLElement>('.js-business-two-factor-confirmation-dialog .Box-title')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const dialogButton = document.querySelector<HTMLElement>('.js-business-two-factor-confirmation-dialog-button')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const businessName = document.querySelector<HTMLInputElement>('#two-factor-business-name')!.value

  hideAllDialogContent()

  if (disablingRequirement) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to remove the two-factor authentication requirement for all organizations in ${businessName}?`,
      '#tfrc-disabling-requirement',
      true,
    )
  } else if (enablingRequirement && enablingSecureMethods) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to require two-factor authentication and only allow secure methods for all organizations in ${businessName}?`,
      '#tfrc-enabling-requirement-with-secure-methods',
      false,
    )
  } else if (enablingRequirement) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to require two-factor authentication for all organizations in ${businessName}?`,
      '#tfrc-enabling-requirement-without-secure-methods',
      false,
    )
  } else if (disablingSecureMethods) {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to allow all two-factor authentication methods for all organizations in ${businessName}?`,
      '#tfrc-disabling-secure-methods-only',
      true,
    )
  } else {
    setDialogState(
      dialogTitle,
      dialogButton,
      `Are you sure you want to only allow secure methods of two-factor authentication for all organizations in ${businessName}?`,
      '#tfrc-enabling-secure-methods-only',
      false,
    )
  }
}

// Updates the two-factor requirement settings dialog. Because the dialog content is dependent on the state of the
// two nested checkboxes, we are unable to predict the dialog content on page load; rather we have to do it here
// whenever the state of either checkbox changes.
function updateTwoFactorConfirmationDialog() {
  if (isEnterprise()) {
    updateTwoFactorConfirmationDialogForEnterprise()
  } else {
    updateTwoFactorConfirmationDialogForDotcom()
  }
}

function shouldPreventDialogForEnterprise(): boolean {
  return !twoFactorRequirementSettingChanged()
}

function shouldPreventDialogForDotcom(): boolean {
  if (!twoFactorSecureMethodsPresent()) {
    return !enablingTwoFactor()
  }
  return !twoFactorRequirementSettingChanged() && !twoFactorRequirementSecureMethodSettingChanged()
}

// bypass showing the two-factor confirmation dialog if we're not enabling two-factor. always show the confirmation dialog
// if the new two-factor secure methods setting is not present.
on('click', '.js-business-two-factor-submit-button', function (event) {
  updateTwoFactorConfirmationDialog()

  const shouldPreventDialog = isEnterprise() ? shouldPreventDialogForEnterprise() : shouldPreventDialogForDotcom()

  if (shouldPreventDialog) {
    event.preventDefault()
  }
})

function handleTwoFactorChangeForEnterprise(requirementCheckbox: HTMLInputElement | null) {
  if (requirementCheckbox == null) {
    return
  }
  const hasChanges = twoFactorRequirementSettingChanged()
  updateTwoFactorRequirementSubmitButtonState(hasChanges)
}

function handleTwoFactorChangeForDotcom(
  requirementCheckbox: HTMLInputElement | null,
  secureMethodsRow: HTMLElement | null,
  secureMethodsCheckbox: HTMLInputElement | null,
  warningBanner: HTMLElement | null,
) {
  if (requirementCheckbox == null || secureMethodsRow == null || warningBanner == null) {
    return
  }

  if (requirementCheckbox.checked && secureMethodsCheckbox && !secureMethodsCheckbox.checked) {
    secureMethodsCheckbox.checked = true
  }

  setTwoFactorRequirementCheckboxState(requirementCheckbox, secureMethodsCheckbox?.checked ?? false)
  const hasChanges = twoFactorRequirementSettingChanged() || twoFactorRequirementSecureMethodSettingChanged()

  if (hasChanges && warningBanner) {
    warningBanner.hidden = !requirementCheckbox?.checked && !secureMethodsCheckbox?.checked
  }
  updateTwoFactorRequirementSubmitButtonState(hasChanges)
}

on('change', '.js-business-enable-two-factor', function () {
  const requirementCheckbox = document.querySelector<HTMLInputElement>('.js-business-enable-two-factor')
  const secureMethodsRow = document.querySelector<HTMLElement>('.js-two-factor-secure-methods')
  const secureMethodsCheckbox = document.querySelector<HTMLInputElement>(
    '.js-business-enable-two-factor-secure-methods',
  )
  const warningBanner = document.querySelector<HTMLElement>('.js-business-two-factor-requirement-warning-banner')

  if (isEnterprise()) {
    handleTwoFactorChangeForEnterprise(requirementCheckbox)
  } else {
    handleTwoFactorChangeForDotcom(requirementCheckbox, secureMethodsRow, secureMethodsCheckbox, warningBanner)
  }
})

on('change', '.js-business-enable-two-factor-secure-methods', function () {
  const requirementCheckbox = document.querySelector<HTMLInputElement>('.js-business-enable-two-factor')
  const secureMethodsCheckbox = document.querySelector<HTMLInputElement>(
    '.js-business-enable-two-factor-secure-methods',
  )
  const warningBanner = document.querySelector<HTMLElement>('.js-business-two-factor-requirement-warning-banner')
  if (requirementCheckbox == null || secureMethodsCheckbox == null || warningBanner == null) {
    // need to tolerate these being missing from the DOM if the feature hasn't been fully rolled out
    return
  }

  // automatically select the 2FA requirement checkbox if the user is enabling the secure methods only checkbox
  if (secureMethodsCheckbox?.checked && requirementCheckbox && !requirementCheckbox.checked) {
    requirementCheckbox.checked = true
  }

  setTwoFactorRequirementCheckboxState(requirementCheckbox, secureMethodsCheckbox?.checked ?? false)

  // show warning banner if there's any new changes
  const hasChanges = twoFactorRequirementSettingChanged() || twoFactorRequirementSecureMethodSettingChanged()
  if (hasChanges && warningBanner) {
    warningBanner.hidden = !requirementCheckbox?.checked && !secureMethodsCheckbox?.checked
  }
  updateTwoFactorRequirementSubmitButtonState(hasChanges)
})

on('change', '.js-repo-types-allowed-radio', () => {
  const repoTypeCheckboxes = document.querySelectorAll<HTMLInputElement>('.js-repo-type-creation-checkbox')
  const repoTypeLabels = document.querySelectorAll<HTMLElement>('.js-repo-type-creation-label')
  for (const checkbox of repoTypeCheckboxes) {
    checkbox.disabled = false
  }
  for (const label of repoTypeLabels) {
    label.classList.remove('color-fg-muted')
  }
})

on('change', '.js-repo-types-disallowed-radio', () => {
  const repoTypeCheckboxes = document.querySelectorAll<HTMLInputElement>('.js-repo-type-creation-checkbox')
  const repoTypeLabels = document.querySelectorAll<HTMLElement>('.js-repo-type-creation-label')
  for (const checkbox of repoTypeCheckboxes) {
    checkbox.disabled = true
    checkbox.checked = false
  }
  for (const label of repoTypeLabels) {
    label.classList.add('color-fg-muted')
  }
})

on('change', '.js-provisioning-enabled-checkbox', () => {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const provisioningEnabledPreviously = document.querySelector<HTMLInputElement>(
    '[name="business_saml_provider_provisioning_enabled_previously"]',
  )!

  if (provisioningEnabledPreviously.value === '0') {
    // setting disabled on the server, nothing to do yet.
    return
  }

  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const userProvisioningCheckbox = document.querySelector<HTMLInputElement>('.js-provisioning-enabled-checkbox')!
  const deprovisioningCheckboxes = document.querySelectorAll<HTMLInputElement>(
    '.js-disable-when-user-provisioning-disabled-checkbox',
  )

  if (userProvisioningCheckbox.checked) {
    for (const checkbox of deprovisioningCheckboxes) {
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const previousValueInput = document.querySelector<HTMLInputElement>(`[name='${checkbox.id}_previously']`)!
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const formCheckbox = checkbox.closest<HTMLElement>('.js-deprovisioning-checkbox')!

      formCheckbox.classList.remove('checkbox-disabled')
      checkbox.removeAttribute('disabled')

      if (previousValueInput.value === '1') {
        // restore state for the last known server value
        checkbox.checked = true
      } else {
        checkbox.removeAttribute('checked')
      }
    }
  } else {
    // un-check, disable
    for (const checkbox of deprovisioningCheckboxes) {
      checkbox.setAttribute('disabled', 'disabled')
      checkbox.checked = false
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      checkbox.closest<HTMLElement>('.js-deprovisioning-checkbox')!.classList.add('checkbox-disabled')
    }
  }
})

// view updates when user switches enterprise package setting
on('change', '.js-enterprise-package-settings-radio', event => {
  const elements = getPackagesElements()
  const target = event.currentTarget

  if (target == null) {
    return
  }

  const packageSettingValue = target.getAttribute('data-package-setting-value')

  for (const checkbox of elements.packageTypeCheckboxes) {
    if (packageSettingValue === 'managed') {
      checkbox.hidden = false
    } else {
      checkbox.hidden = true
    }
  }

  elements.packageTypeList.hidden = packageSettingValue === 'disabled'
  elements.packageSettingsManagedWarning.hidden = packageSettingValue !== 'managed'
  elements.packageSettingsDisabledWarning.hidden = packageSettingValue !== 'disabled'
  elements.updateSettingsButton.disabled = false
})

on('change', '.js-package-type-checkbox', () => {
  const elements = getPackagesElements()
  elements.updateSettingsButton.disabled = false
})

function getPackagesElements() {
  const packageTypeCheckboxes = document.querySelectorAll<HTMLInputElement>('.js-package-type-checkbox')
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const packageTypeList = document.querySelector<HTMLElement>('.js-package-types-list')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const packageSettingsManagedWarning = document.querySelector<HTMLElement>(
    '.js-enterprise-package-settings-managed-warning',
  )!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const updateSettingsButton = document.querySelector<HTMLButtonElement>('.js-update-enterprise-package-settings')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const packageSettingsDisabledWarning = document.querySelector<HTMLElement>(
    '.js-enterprise-package-settings-disabled-warning',
  )!

  return {
    packageTypeCheckboxes,
    packageTypeList,
    packageSettingsManagedWarning,
    updateSettingsButton,
    packageSettingsDisabledWarning,
  }
}

function toggleBillingInformationEditForm(shown: boolean) {
  const editButtons = document.querySelectorAll<HTMLElement>('.js-billing-settings-billing-information-edit-button')
  for (const editButton of editButtons) {
    editButton.hidden = shown
  }

  const detailElements = document.querySelectorAll<HTMLElement>('.js-billing-settings-billing-information')
  for (const detailElement of detailElements) {
    detailElement.hidden = shown
  }

  const editFormContainer = document.querySelector<HTMLElement>(
    '.js-billing-settings-billing-information-form-container',
  )
  if (editFormContainer) {
    editFormContainer.hidden = !shown
  }
}

on('click', '.js-billing-settings-billing-information-edit-button', function () {
  toggleBillingInformationEditForm(true)
})

on('click', '.js-billing-settings-billing-information-cancel-button', function () {
  toggleBillingInformationEditForm(false)
})

function toggleBillingInvoiceEmailPreferenceForm(shown: boolean) {
  const editButtons = document.querySelectorAll<HTMLElement>(
    '.js-billing-settings-billing-invoice-email-preference-edit-button',
  )
  for (const editButton of editButtons) {
    editButton.hidden = shown
  }

  const editFormContainer = document.querySelector<HTMLElement>(
    '.js-billing-settings-billing-invoice-preference-form-container',
  )
  if (editFormContainer) {
    editFormContainer.hidden = !shown
  }
}

on('click', '.js-billing-settings-billing-invoice-email-preference-edit-button', function () {
  toggleBillingInvoiceEmailPreferenceForm(true)
})

on('change', '.js-pat-expiration-limit', event => {
  const target = event.target as HTMLInputElement
  if (target.value === 'custom') {
    document.getElementById('custom-pat-expiration-limit-wrapper')?.removeAttribute('hidden')
  } else {
    document.getElementById('custom-pat-expiration-limit-wrapper')?.setAttribute('hidden', 'true')
  }
})

document.addEventListener('turbo:frame-load', function () {
  const dialog = document.getElementById('confirm-lifetimes') as HTMLDialogElement
  if (dialog) {
    dialog.showModal()
  }
})
